/*
 * Copyright (c) 2024 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain
 * proprietary information of OCLC, Inc. and shall not be disclosed in whole or in
 * any part to any third party or used by any person for any purpose, without written
 * consent of OCLC, Inc.  Duplication of any portion of these  materials shall include this notice.
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Link } from '@mui/material';

import Layout from '../Layout';
import PageContent from '../PageContent';
import ErrorDetails from '../ErrorDetails';

import commonActions from '../../utils/actions';
import messages from '../../utils/messages';

/**
 * Generates a link to email support.
 *
 * @param {*} content
 * @returns the email support link.
 */
const link = (content) => (
  <Link
    id='mail-to-support'
    data-testid='mail-to-support'
    href='mailto:support@oclc.org'
  >
    {content}
  </Link>
);

/**
 * Component that builds the ServerErrorPage.
 * @return {*}
 * @constructor
 */
const ServerErrorPage = ({
  errorCode = messages.INTERNAL_SERVER_ERROR.id,
  errorMessage = messages.INTERNAL_SERVER_ERROR.defaultMessage,
  branding = {},
  parameters,
  statusCode = 500,
}) => {
  let heading, instructionalText;

  if (statusCode >= 400 && statusCode < 500) {
    heading = commonActions.getClientErrorHeading();
    instructionalText = commonActions.getClientErrorInstructionalText({
      link: link,
    });
  } else {
    heading = commonActions.getServerErrorHeading();
    instructionalText = commonActions.getServerErrorInstructionalText({
      link: link,
    });
  }

  const { displayName } = branding;
  return (
    <Layout
      pageTitle={`${heading} ${displayName ? `- ${displayName}` : ''}`}
      branding={branding}
      containerStyles={{
        maxWidth: { xs: '800px' },
      }}
    >
      <PageContent
        id='content'
        headingId='content-header'
        heading={heading}
        instructionalText={instructionalText}
      >
        <ErrorDetails
          error={
            messages[errorCode]
              ? commonActions.getText(messages[errorCode])
              : errorMessage
          }
          parameters={parameters}
          statusCode={statusCode}
        />
      </PageContent>
    </Layout>
  );
};

ServerErrorPage.propTypes = {
  branding: PropTypes.object,
  errorCode: PropTypes.string,
  errorMessage: PropTypes.string,
  parameters: PropTypes.string,
  statusCode: PropTypes.number,
};

export default ServerErrorPage;
